import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import WorkTemplatesContext from '@atom/components/workTemplates/WorkTemplatesContext';
import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon, FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { WorkTemplateFolderTree } from '@atom/types/workTemplate';

import FolderOptions from './FolderOptions';

import classes from './workTemplatesFolderTree.module.css';

const PADDING_OFFSET = 2;

const styles = {
  iconStyle: {
    fontSize: '24px',
    margin: 0,
  },
  blankStyle: {
    boxSizing: 'border-box',
    padding: '24px',
  },
};

export interface Props {
  folder: WorkTemplateFolderTree;
  indentation: number;
}

const WorkTemplatesFolderRow = ({ folder, indentation }: Props) => {
  const {
    open,
    toggleOpen,
    setActiveFolderId,
    activeFolderId,
    setIsActiveFolderLeafNode,
    setWorkTemplatesView,
  } = useContext(WorkTemplatesContext);
  const expanded = open.has(folder.id);
  const [overflowing, setOverflowing] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [folder.name]);

  useEffect(() => {
    if (activeFolderId === folder.id) {
      setIsActiveFolderLeafNode(R.isEmpty(folder.children));
    }
  }, [setIsActiveFolderLeafNode, folder.children, activeFolderId, folder.id]);

  const arrowDirection = expanded ? ArrowDropDownIcon : ArrowRightIcon;

  const leftIcon = (
    <>
      {!R.isEmpty(folder.children) ? (
        <IconButton
          onClick={() => toggleOpen(folder.id)}
          style={styles.iconStyle}
        >
          <Icon color={colors.neutral.dim} icon={arrowDirection} />
        </IconButton>
      ) : (
        <div style={styles.blankStyle} />
      )}
      <Icon
        style={styles.iconStyle}
        color={colors.neutral.gray}
        icon={FolderIcon}
      />
    </>
  );

  const containerStyle = {
    paddingLeft: `calc(1rem + ${PADDING_OFFSET * indentation}rem)`,
  };

  const onClick = () => {
    setActiveFolderId(folder.id);
    setIsActiveFolderLeafNode(R.isEmpty(folder.children));
    setWorkTemplatesView({ page: 1 });
  };

  const overflowStyle = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: `calc(12rem - (1rem + ${PADDING_OFFSET * indentation}rem))`,
  };

  return (
    <>
      <div
        className={clsx(
          classes.folderRow,
          folder.id === activeFolderId && classes.selected,
        )}
        style={containerStyle}
        onClick={onClick}
      >
        <div ref={container} className={classes.folderNameContainer}>
          {leftIcon}
          <div className={classes.rowNameContainer}>
            <div>
              {!overflowing ? (
                folder.name
              ) : (
                <Tooltip
                  lightTooltip
                  title={folder.name}
                  placement="right-start"
                >
                  <p style={overflowStyle}>{folder.name}</p>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <FolderOptions folder={folder} />
      </div>
      {expanded ? (
        folder.children.map((child: WorkTemplateFolderTree) => {
          return (
            <WorkTemplatesFolderRow
              key={child.id}
              folder={child}
              indentation={indentation + 1}
            />
          );
        })
      ) : (
        <div />
      )}
    </>
  );
};

export default WorkTemplatesFolderRow;
